import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Table from "components/views/atoms/Table";
import Spinner from "components/views/atoms/Spinner";
import T from "i18n";

import {
  attributeIds,
  colRenderer,
} from "components/utils/ProductAttributesMapping";

const Wrapper = styled.div`
  position: relative;

  .legend {
    font-size:0.8em;
    margin-top:10px;

    .legendItem {
      margin:0 30px 0 10px;
        display:block;
        width:100%;
        margin-bottom:5px;

      ${({ theme }) => theme.mediaQueries.medium} {
        display:inline-block;
        width:auto;
      }

      .icon {
        padding-right:10px;

        &.syntheticOil {
          color:red;
        }

        &.bioGas {
          color:green;
        }
      }
    }

    span {
      vertical-align: text-top;
    }
  }

  &.loading {
    min-height: 100px;
  }

  .naturalGasProduct {  
      &:after {
        content: " (N)";  
    }  
   }

  .syntheticProduct {  
      &:after {
        content: " (S)";
        color: red;  
    }
  }

  .bioProduct {  
      color:green;      
      &:after {
        content: " (B)";
      }    
  }

  tr {
    td {
      a{
        color:#004C9B;
        cursor:pointer;
        display: inline-block;
        text-decoration:none;
        outline: none;
        &:focus, &:hover{
          text-decoration:underline;
        }
      }
    }
  }
`;

const ProductsTableView = ({ className, list, showLegend }) => {
  const classes = ["ProductsTableView"];
  if (className) classes.push(className);
  if (list === undefined) classes.push("loading");
  const [orderBy, setOrderBy] = React.useState();
    const clonedList = list && [...list];

  if (orderBy)
    clonedList.sort((a, b) => {
      switch (typeof a[orderBy.colName]) {
        case "number":
          return orderBy.ascending
            ? a[orderBy.colName] - b[orderBy.colName]
            : b[orderBy.colName] - a[orderBy.colName];

        case "string":
          if (orderBy.ascending) {
            return a[orderBy.colName].toUpperCase() >
              b[orderBy.colName].toUpperCase()
              ? 1
              : -1;
          }
          return a[orderBy.colName].toUpperCase() >
            b[orderBy.colName].toUpperCase()
            ? -1
            : 1;

        default:
          return 1;
      }
    });

  function onColClick(colName) {
    return () => {
      setOrderBy((orderData) => ({
        colName,
        ascending:
          orderData?.colName === colName ? !orderData.ascending : false,
      }));
    };
  }

  const legendSyntheticOil = T({ path: "productTable.syntheticOil" });
  const legendNaturalGas = T({ path: "productTable.includesNaturalGas" });
  const legendSuitableBioLandSewage = T({ path: "productTable.suitableBioLandSewage" });
  
  const attributeLabelsMap = {
    name: T({path: "productTable.productName"}),
    engine_cleanliness: T({path: "productTable.engineCleanliness"}),
    oxidation_nitration: T({path: "productTable.oxidationResistance"}),
    performance_odi: T({path: "productTable.oilDrainInterval"}),
    sae_grade: T({path: "productTable.saeGrade"}),
    sulfated_ash_percentage: T({path: "productTable.sulphatedAsh"}),
    tbn: T({path: "productTable.totalBaseNumber"}),
    wear_protection: T({path: "productTable.wearProtection"}),
  };
  
  return (
    <Wrapper className={classes}>
      {clonedList === undefined && <Spinner relativeToParent />}
      {clonedList && (
        <>
          <Table>
            <thead>
              <tr>
                {attributeIds.map((colName) => {
                  const isOrdered = orderBy?.colName === colName;
                  const orderDirection = orderBy?.ascending
                    ? "ascending"
                    : "descending";
                  return (
                    <th key={colName}>
                      <button
                        type="button"
                        onClick={onColClick(colName)}
                        className={isOrdered ? orderDirection : ""}
                      >
                        {attributeLabelsMap[colName]}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {clonedList?.map((prod) => {                
                const categories = [];
                if (prod.natural_gas_product) {
                    categories.push("naturalGasProduct");
                } 
                if (prod.synthetic_oil_product) {
                    categories.push("syntheticProduct");
                }
                if (prod.suitable_for_bio_landfill_sewage) {
                    categories.push("bioProduct");
                }
                                
                return (
                  <tr key={prod.product_node_id}>
                    {attributeIds.map((colName) => (
                      <td key={colName}>
                        {(colName === "name" && (prod.product_url || prod.product_page_link)
                                ? (
                                  <span key={colName}>
                                    <a target="_blank" rel="noreferrer" href={(prod.product_url ? prod.product_url : prod.product_page_link)}>
                                      {colRenderer(colName, prod[colName])}                                                                                                        
                                    </a>
                                    {categories.map((cat) => <span key={cat} className={cat} />)}
                                  </span>
                                  )                                
                          : colRenderer(colName, prod[colName]))}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </Table>
          
          {(!showLegend ? null : 
            (
              <p className="legend">
                <span className="legendItem">
                  <span className="icon syntheticOil">(S)</span>
                  <span className="text">{legendSyntheticOil}</span>
                </span>
                <span className="legendItem">
                  <span className="icon naturalGas">(N)</span>
                  <span className="text">{legendNaturalGas}</span>
                </span>
                <span className="legendItem">
                  <span className="icon bioGas">(B)</span>
                  <span className="text">{legendSuitableBioLandSewage}</span>
                </span>
              </p>
            )
          )}
        </>
      )}
    </Wrapper>
  );
};

ProductsTableView.displayName = "ProductsTableView";

ProductsTableView.propTypes = {
  className: PropTypes.string,
  list: PropTypes.arrayOf(PropTypes.shape({})),
  showLegend: PropTypes.bool
};

ProductsTableView.defaultProps = {
  className: null,
  list: undefined,
  showLegend: true,
};

export default ProductsTableView;
